import { getUnlockTarget } from "@/lib/unlockTarget";

export interface PendingOrder {
  address: string;
  propertyType?: string;
  apartmentNumber?: string;
}

/**
 * The report the buyer picked on the home page before checkout (stored by
 * Hero), sent with create-payment so it survives a guest checkout: a guest
 * signs in later through an emailed magic link, in a tab where this
 * sessionStorage is gone, and that link opens the order form prefilled with it.
 *
 * Same keys /payment-success auto-orders from for a signed-in buyer. Null for
 * an unlock purchase, whose credit is earmarked for the preview it unlocks.
 */
export function getPendingOrder(): PendingOrder | null {
  if (typeof window === "undefined") return null;
  try {
    if (getUnlockTarget()) return null;
    const address = sessionStorage.getItem("selectedAddress");
    if (!address) return null;
    return {
      address,
      propertyType: sessionStorage.getItem("selectedPropertyType") || undefined,
      apartmentNumber: sessionStorage.getItem("selectedApartmentNumber") || undefined,
    };
  } catch {
    // Storage blocked (private mode): nothing to carry over.
    return null;
  }
}

const PENDING_ORDER_KEYS = ["selectedAddress", "selectedPropertyType", "selectedApartmentNumber"] as const;

/**
 * Forget the picked report once it has been handed over (to the dashboard
 * form) or ordered. Left behind, it is still "the report to order after
 * payment": a later credit purchase in the same tab would spend a credit on it
 * again.
 */
export function clearPendingOrder(onlyIfAddress?: string): void {
  if (typeof window === "undefined") return;
  try {
    if (onlyIfAddress !== undefined && sessionStorage.getItem("selectedAddress") !== onlyIfAddress) return;
    PENDING_ORDER_KEYS.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage blocked: nothing was stored either.
  }
}

/** Query string the dashboard order form prefills from (see NewReportSection). */
export function orderFormSearch(order: PendingOrder): string {
  const params = new URLSearchParams({ address: order.address });
  if (order.propertyType) params.set("type", order.propertyType);
  if (order.apartmentNumber) params.set("apartment", order.apartmentNumber);
  return `?${params.toString()}`;
}
